import type {ImageField, KeyTextField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {linkResolver} from 'prismicio'

type HighlightsCardProps = {
  icon?: ImageField
  headline: KeyTextField
  body: KeyTextField
  body_rte?: RichTextField
  v2?: boolean
}

export const HighlightsCard = ({icon, headline, body, body_rte, v2 = false}: HighlightsCardProps) => {
  const hasIcon = isFilled.image(icon)
  const hasRTE = isFilled.richText(body_rte)

  return (
    <>
      {hasIcon && <IconHighlightCard icon={icon} v2={v2} />}
      <div className="flex flex-col gap-3">
        <h3
          className={cn('text-moss-black', {
            'text-20': hasIcon && !v2,
            'font-medium leading-7': hasIcon,
            'text-18': v2,
            'text-h0': !hasIcon,
          })}
        >
          {headline}
        </h3>
        <p
          className={cn({
            'text-body text-grey-550': !v2,
            'text-moss-grey-400 text-15 leading-[22px]': v2,
          })}
        >
          {body}
        </p>
        {hasRTE && (
          <PrismicRichText
            field={body_rte}
            components={{
              paragraph: ({children}) => <p className="text-body text-grey-550">{children}</p>,
              hyperlink: ({children, node}) => (
                <Link
                  className="text-body text-center text-grey-550 underline underline-offset-2 md:text-start"
                  href={linkResolver(node.data)}
                  {...(isFilled.link(node.data) && node.data.link_type === 'Web' && {target: node.data.target})}
                >
                  {children}
                </Link>
              ),
            }}
          />
        )}
      </div>
    </>
  )
}

const IconHighlightCard = ({icon, v2 = false}: {icon: ImageField; v2?: boolean}) =>
  v2 ? (
    <div className="relative flex size-8 items-center justify-center rounded-md bg-moss-white">
      <PrismicNextImage field={icon} className="size-5 object-cover" />
      <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
    </div>
  ) : (
    <PrismicNextImage field={icon} className="size-5" />
  )
